import React, { useRef } from 'react'

import pluralize from 'pluralize'

import { Alert, Button } from 'antd'

import { observer } from 'mobx-react-lite'

import { DrawerRef } from 'common/components/Drawer'

import { UpgradeDrawer } from 'vendor/components/Billing'
import { useVendorStores } from 'vendor/hooks/use-stores'

const BillingAlert = observer(() => {
  const { userStore } = useVendorStores()

  const { stripe_customer_id } = userStore.currentVendor.billing
  // If there is not even a customer id then we must need to tell the user they are able to upgrade
  if (!stripe_customer_id) {
    return <UpgradeAlert />
  } else {
    return <SeatsAvailableAlert />
  }
})

const UpgradeAlert = () => {
  const drawerUpdateRef = useRef<DrawerRef>()
  return (
    <>
      <Alert
        message="Upgrade to Premium"
        description="All your users are currently on the free tier of Subbase, want to learn about how upgrading to premium can help your business?"
        type="info"
        showIcon
        action={
          <Button
            type="primary"
            onClick={() => {
              drawerUpdateRef.current.show()
            }}
          >
            Learn More
          </Button>
        }
      />
      <UpgradeDrawer ref={drawerUpdateRef} />
    </>
  )
}

const SeatsAvailableAlert = observer(() => {
  const { vendorSettingStore, userStore } = useVendorStores()
  const taken = vendorSettingStore.actualNumPremiumUsers
  const purchased = userStore.numPremiumSeatsPurchased
  const remaining = purchased - taken

  let type: 'info' | 'error' = 'info'
  let message = ''
  let description = `You have currently purchased ${pluralize('premium seat', purchased, true)}. `
  if (remaining == 0) {
    message = 'All seats used!'
    description += `If you'd like to upgrade additional users, use your billing portal to purchase more seats.`
  } else if (remaining < 0) {
    type = 'error'
    message = `Downgrade ${pluralize('user', -1 * remaining, true)} to Free Tier`
    description += `However, you have ${pluralize('user', taken, true)} assigned to the premium tier.
    Please purchase additional seats or downgrade certain users. Failure to do so will result in arbitrary users being downgraded.`
  } else {
    message = `${pluralize('Premium Seats', remaining, true)} Available`
    description = `You are currently utilizing ${taken} / ${purchased} ${pluralize(
      'seat',
      purchased,
    )}. Please select which additional users you would like to upgrade to premium.`
  }

  return (
    <>
      <Alert
        message={message}
        description={description}
        type={type}
        showIcon
        action={
          <Button type="primary" href="/vendor/billing/billing_session">
            Billing
          </Button>
        }
      />
    </>
  )
})

export default BillingAlert
